import { getOrder } from "../../../api/SetOrderAPI";
import { createOrderDetail } from "./orderDetailService";

const getSessionPayload = (response) => response?.data?.data ?? response?.data;
const DEVELOPMENT_SESSION_IDS = new Set(["dev", "local", "vs_dev"]);

const isRecord = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const parseRecord = (value) => {
  if (isRecord(value)) return value;
  if (typeof value !== "string") return {};

  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const isDevelopmentSession = (sessionId) =>
  process.env.NODE_ENV === "development" &&
  DEVELOPMENT_SESSION_IDS.has(String(sessionId).trim().toLowerCase());

const createDevelopmentSession = (sessionId) => ({
  sessionId,
  status: "READY",
  currentStep: "INTRO",
  isDevelopmentSession: true,
});

const SITE_CONFIG_FIELDS = Object.freeze([
  "tgid",
  "fbqToken",
  "pixel",
  "amount",
  "amount_fast",
  "groupid",
  "googlePixel",
  "googleConversionLabel",
  "tiktokPixel",
  "adPlatform",
]);

const getOrderResponse = (payload) =>
  isRecord(payload?.order) ? payload.order : null;

const getSiteConfigResponse = (payload) =>
  isRecord(payload?.url) ? payload.url : {};

const getText = (value) => (typeof value === "string" ? value.trim() : "");

export const getVerificationSiteUrl = (payload) => {
  const order = getOrderResponse(payload);
  const source = getSiteConfigResponse(payload);

  // 后端历史上返回过多种字段位置。优先使用明确的 site_url，
  // 同时兼容当前 order.url 和站点配置中的 url。
  return getText(
    payload?.site_url ||
      payload?.siteUrl ||
      source.site_url ||
      source.siteUrl ||
      order?.site_url ||
      order?.siteUrl ||
      order?.url ||
      source.url,
  );
};

export const getVerificationSuccessUrl = (payload) => {
  const order = getOrderResponse(payload);
  const orderData = parseRecord(order?.data);
  const source = getSiteConfigResponse(payload);

  return getText(
    payload?.success_url ||
      payload?.successUrl ||
      source.success_url ||
      source.successUrl ||
      order?.success_url ||
      order?.successUrl ||
      orderData.success_url ||
      orderData.successUrl,
  );
};

export const getVerificationSiteConfig = (payload) => {
  const source = getSiteConfigResponse(payload);
  const config = SITE_CONFIG_FIELDS.reduce((result, field) => {
    result[field] = source[field] ?? "";
    return result;
  }, {});

  config.tiktokPixel = getText(
    source.tiktokPixel ||
      source.tikTokPixel ||
      source.tiktok_pixel ||
      source.tiktok_pixel_id,
  );

  return {
    siteUrl: getVerificationSiteUrl(payload),
    ...config,
  };
};

export const normalizeVerificationSession = (
  payload,
  requestedSessionId = "",
) => {
  const order = getOrderResponse(payload);
  if (!order) return null;

  const orderData = parseRecord(order.data);
  const siteUrl = getVerificationSiteUrl(payload);
  const successUrl = getVerificationSuccessUrl(payload);
  const siteConfig = getVerificationSiteConfig(payload);
  const { session_id: responseSessionId = "", status = "READY" } = order;
  const { currentStep = "INTRO", locale = "" } = payload;
  const returnUrl = getText(payload.returnUrl || payload.return_url);
  const sessionId = responseSessionId || requestedSessionId;
  const orderDetail = createOrderDetail(orderData);

  return {
    sessionId,
    orderId: orderDetail.id,
    orderNu: orderDetail.orderNu,
    status,
    currentStep,
    locale,
    successUrl,
    returnUrl,
    orderDetail,
    siteUrl,
    siteConfig,
  };
};

const VERIFIED_STATUSES = new Set([
  "VERIFIED",
  "APPROVED",
  "SUCCESS",
  "COMPLETED",
  "COMPLETE",
]);
const FAILED_STATUSES = new Set(["FAILED", "REJECTED", "DECLINED"]);

export const normalizeVerificationStatus = (session) => {
  const status = String(session?.status ?? "PROCESSING")
    .trim()
    .toUpperCase();

  if (VERIFIED_STATUSES.has(status)) return "verified";
  if (FAILED_STATUSES.has(status)) return "failed";
  if (status === "EXPIRED") return "expired";
  return "processing";
};

const getSession = async (sessionId, signal) => {
  if (!sessionId) {
    throw new Error("A verification session ID is required.");
  }

  let response;

  try {
    response = await getOrder(sessionId, signal);
  } catch (error) {
    if (isDevelopmentSession(sessionId)) {
      return createDevelopmentSession(sessionId);
    }
    throw error;
  }
  const session = normalizeVerificationSession(
    getSessionPayload(response),
    sessionId,
  );

  if (!session || typeof session !== "object") {
    throw new Error("The verification session response is invalid.");
  }

  return session;
};

export const verificationService = {
  getSession,

  async getVerificationStatus(sessionId, signal) {
    const session = await getSession(sessionId, signal);
    return {
      status: normalizeVerificationStatus(session),
      session,
    };
  },
};
